import { ANALYZERS, ENTITY_TYPE } from "@/settings/settings.js";

import { type AnalyzerIdMapping, type AppConfiguration, type IdRangePair } from "../graphql.js";

export function plsqlBasePath(appConfiguration: AppConfiguration): string {
  const classicHomePage: string = appConfiguration && appConfiguration.baseUrlClassic;

  return classicHomePage && classicHomePage.substring(0, classicHomePage.lastIndexOf("/")) + "/";
}

export function urlHomePage(appConfiguration: AppConfiguration) {
  return appConfiguration && plsqlBasePath(appConfiguration) + "phy_index.home";
}

export function urlRedirectToGroupPage(appConfiguration: AppConfiguration, groupTypeId: number, groupId: number) {
  const uriRedirectToGroup: string = "phy_history.redirect" + "?p_grt_id=" + groupTypeId + "&p_grp_ids=" + groupId;

  return appConfiguration && plsqlBasePath(appConfiguration) + uriRedirectToGroup;
}

export function urlJnlpPage(
  appConfiguration: AppConfiguration,
  analyzerInputIds: AnalyzerIdMapping[],
  analyzer: string,
) {
  let plsqlRoutine: string = null;
  let params = "";

  switch (analyzer) {
    case ANALYZERS.LOCUS_ANALYZER:
      plsqlRoutine = "la_jnlp";
      params = "&p_load_orfs=true";
      break;
    case ANALYZERS.DNA_ANALYZER:
    case ANALYZERS.PROTEIN_ANALYZER:
      plsqlRoutine = "pb_jnlp";
      break;
    case ANALYZERS.PATHWAY_ANALYZER:
      plsqlRoutine = "pn_jnlp";
      break;
    default:
      plsqlRoutine = "la_jnlp";
  }

  let jnlpUrl: string = "phy_setup." + plsqlRoutine + "?";

  // The following defines which label has to be used for the various types of IDs,
  // by analyzer.
  // Only "simple" entity types should be encountered here, like protein, gene, transcript, organism...
  // (no proteinLocations etc.)
  analyzerInputIds.forEach((set: AnalyzerIdMapping, setIdx: number) => {
    let idLabel: string = null;

    switch (set.entityType) {
      case ENTITY_TYPE.GENE:
        idLabel = "p_gnu_ids";
        break;
      case ENTITY_TYPE.PATHWAY:
        idLabel = "p_pat_ids";
        break;
      case ENTITY_TYPE.PROTEIN:
        switch (analyzer) {
          case ANALYZERS.LOCUS_ANALYZER:
          case ANALYZERS.PATHWAY_ANALYZER:
            idLabel = "p_prs_ids";
            break;
          case ANALYZERS.PROTEIN_ANALYZER:
          case ANALYZERS.DNA_ANALYZER:
            idLabel = "p_ges_ids";
            break;
          default:
            idLabel = "p_prs_ids";
            break;
        }
        break;
      case ENTITY_TYPE.TRANSCRIPT:
        switch (analyzer) {
          case ANALYZERS.PROTEIN_ANALYZER:
          case ANALYZERS.DNA_ANALYZER:
            idLabel = "p_ges_ids";
            params += "&p_seq_type=39";
            break;
          default:
            idLabel = "p_nus_ids";
            break;
        }
        break;
      case ENTITY_TYPE.PROBE:
        switch (analyzer) {
          case ANALYZERS.DNA_ANALYZER:
            idLabel = "p_ges_ids";
            params += "&p_seq_type=48";
            break;
          default:
            idLabel = "p_nus_ids";
            break;
        }
        break;
      case ENTITY_TYPE.OTHER:
        switch (analyzer) {
          case ANALYZERS.DNA_ANALYZER:
            idLabel = "p_ges_ids";
            params += "&p_seq_type=46";
            break;
          default:
            idLabel = "p_nus_ids";
            break;
        }
        break;
      case ENTITY_TYPE.CONTIG:
        switch (analyzer) {
          case ANALYZERS.PROTEIN_ANALYZER:
          case ANALYZERS.DNA_ANALYZER:
            idLabel = "p_ges_ids";
            params += "&p_seq_type=30";
            params += "&p_show_features=Genes";
            break;
          case ANALYZERS.LOCUS_ANALYZER:
            idLabel = "p_nus_ids";
            break;
          default:
            idLabel = "p_nus_ids";
            break;
        }
        break;
      case ENTITY_TYPE.ORGANISM:
        // only accepted by pathway analyzer as p_org_ids
        idLabel = "p_org_ids";
        break;
      case ENTITY_TYPE.PROJECT_FILE:
        switch (analyzer) {
          case ANALYZERS.DNA_ANALYZER:
            idLabel = "p_cfi_id";
            params += "&p_seq_type=30";
            break;
          default:
            idLabel = "p_cfi_id";
            break;
        }
        break;
      default:
        idLabel = "p_prs_ids";
        break;
    }

    set.idRanges.forEach((idRange: IdRangePair, idIdx: number) => {
      jnlpUrl += `${setIdx !== 0 || idIdx !== 0 ? "&" : ""}${idLabel}=${idRange.id}`;
      // only used for sequence analyzer startup from genes
      if (idRange.range) {
        params += `&p_regions=${idRange.range.start}-${idRange.range.stop}`;
      }
    });
  });

  jnlpUrl += params;

  return appConfiguration && plsqlBasePath(appConfiguration) + jnlpUrl;
}

export function getAnalyzerConversionEntityTypes(entityType: string, analyzer: string): string[] {
  if (analyzer === ANALYZERS.LOCUS_ANALYZER) {
    switch (entityType) {
      case ENTITY_TYPE.PROTEIN:
      case ENTITY_TYPE.PROTEIN_LOCATION:
      case ENTITY_TYPE.TRANSCRIPT:
      case ENTITY_TYPE.TRANSCRIPT_LOCATION:
        return [ENTITY_TYPE.GENE];
      case ENTITY_TYPE.ORGANISM:
        return [ENTITY_TYPE.CONTIG];
      default:
        return null;
    }
  } else if (analyzer === ANALYZERS.DNA_ANALYZER || analyzer === ANALYZERS.PROTEIN_ANALYZER) {
    switch (entityType) {
      case ENTITY_TYPE.GENE:
        return [ENTITY_TYPE.CONTIG];
      case ENTITY_TYPE.PROTEIN_LOCATION:
        return [ENTITY_TYPE.PROTEIN];
      case ENTITY_TYPE.TRANSCRIPT_LOCATION:
        return [ENTITY_TYPE.TRANSCRIPT];
      default:
        return null;
    }
  } else if (analyzer === ANALYZERS.PATHWAY_ANALYZER) {
    return [ENTITY_TYPE.ORGANISM, ENTITY_TYPE.PATHWAY, ENTITY_TYPE.PROTEIN];
  }
}
